/**
 * In-memory GET cache with TTL and inflight deduplication.
 * Makes repeat navigations to the same student page near-instant.
 */

const DEFAULT_TTL_MS = 120_000;

const store = new Map();

function now() {
  return Date.now();
}

export function getCached(key) {
  const entry = store.get(key);
  if (!entry) return null;
  if (entry.expires && entry.expires > now()) {
    return entry.data;
  }
  if (entry.expires && entry.expires <= now()) {
    store.delete(key);
  }
  return null;
}

export function setCached(key, data, ttlMs = DEFAULT_TTL_MS) {
  store.set(key, { data, expires: now() + ttlMs });
}

export function invalidateCache(prefix) {
  for (const key of store.keys()) {
    if (key.startsWith(prefix)) store.delete(key);
  }
}

export function invalidateStudentCache(studentId) {
  invalidateCache(`student:${studentId}:`);
}

export async function cachedRequest(key, fetcher, ttlMs = DEFAULT_TTL_MS) {
  const hit = getCached(key);
  if (hit !== null) {
    return hit;
  }

  const existing = store.get(key);
  if (existing?.inflight) {
    return existing.inflight;
  }

  const inflight = Promise.resolve()
    .then(fetcher)
    .then((data) => {
      setCached(key, data, ttlMs);
      const entry = store.get(key);
      if (entry) delete entry.inflight;
      return data;
    })
    .catch((err) => {
      const entry = store.get(key);
      if (entry) delete entry.inflight;
      throw err;
    });

  store.set(key, { ...(existing || {}), inflight });
  return inflight;
}
